/**
 * Configuración centralizada de opciones de filtros para Invoices/Orders
 * Este archivo define las opciones de filtros que se utilizan tanto en AdvancedFilter
 * como en PrefacturarModal para mantener consistencia en toda la aplicación.
 */
import { FilterStructure } from '@shared/components/ui/AdvancedFilter';

/**
 * Interfaz para el callback de carga de valores dinámicos
 */
export type LoadColumnValuesCallback = (
  columnValue: string,
) => Promise<Array<{ label: string; value: string | number }>>;

/**
 * Interfaz para opciones de país
 */
export interface CountryOption {
  id: string;
  name: string;
  iso2: string;
}

/**
 * Obtiene las opciones de filtros para el módulo de PreInvoices
 * Solo incluye los campos y operadores soportados por la API:
 * - status: in, not_in, is_null, is_not_null
 * - total_amount: in, not_in, is_null, is_not_null
 * - user_id: in, not_in, is_null, is_not_null
 * - created_at: in, not_in, is_null, is_not_null, greater_than, less_than, greater_equal, less_equal
 * - code: in, not_in, is_null, is_not_null, greater_than, less_than, greater_equal, less_equal
 *
 * @param t - Función de traducción del módulo
 * @param loadColumnValues - Callback para cargar valores dinámicos (no usado actualmente)
 * @returns Array de configuración de filtros
 */
export const getPreInvoicesFilterOptions = (
  t: (key: string) => string,
  loadColumnValues: LoadColumnValuesCallback,
): FilterStructure[] => {
  return [
    // 🎯 status
    {
      column: {
        label: t('filters.status', 'Estado'),
        value: 'status',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [
        { label: t('filters.notInvoiced', 'No facturado'), value: 'DRAFT' },
        { label: t('filters.invoiced', 'Facturado'), value: 'CLOSED' },
      ],
    },
    // 🎯 total_amount
    {
      column: {
        label: t('table.total_amount', 'Total Pre-factura'),
        value: 'total_amount',
        type: 'number',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: null, // 🎯 null para input de número libre
    },
    // 🎯 user_id
    {
      column: {
        label: t('filters.userId', 'Usuario'),
        value: 'user_id',
        type: 'number',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: null, // 🎯 null para input de número libre
    },
    // 🎯 created_at
    {
      column: {
        label: t('table.created_at', 'Fecha'),
        value: 'created_at',
        type: 'date',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: [],
    },
    // 🎯 code
    {
      column: {
        label: t('table.code', 'Código'),
        value: 'code',
        type: 'text',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: null, // 🎯 null para input de texto libre
    },
  ];
};
